import { VendorQuoteMessage } from '../types/VendorQuote';

export type VendorThreadStatus = 'Draft' | 'Sent' | 'Replied' | 'Closed';

export type VendorThreadMessage = VendorQuoteMessage & {
  sortValue: number;
  preview: string;
  vendorEmail: string;
  vendorName: string;
};

export type VendorQuoteThread = {
  id: string;
  subject: string;
  vendor: {
    name: string;
    email: string;
  };
  lastActivity?: string;
  messageCount: number;
  status: VendorThreadStatus;
  preview: string;
  messages: VendorThreadMessage[];
};

const getSortValue = (value: string | undefined, fallbackIndex: number) => {
  if (!value) return fallbackIndex;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : fallbackIndex;
};

const normalizeSubject = (subject?: string) => {
  const cleaned = (subject || '').replace(/^(\s*(re|fw|fwd)\s*:\s*)+/gi, '').trim();
  return cleaned || 'No subject';
};

const stripHtml = (html?: string) => {
  if (!html) return '';
  return html
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&#39;/gi, "'")
    .replace(/&quot;/gi, '"');
};

const getMessagePreview = (message: VendorQuoteMessage) => {
  const raw = (message.bodyText && message.bodyText.trim()) || stripHtml(message.bodyHtml);
  return raw.replace(/\s+/g, ' ').trim();
};

const toTitleCase = (value: string) => value
  .split(' ')
  .filter(Boolean)
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
  .join(' ');

const deriveNameFromEmail = (email: string) => {
  if (!email.includes('@')) return '';
  const local = email.split('@')[0].replace(/[._-]+/g, ' ').trim();
  return toTitleCase(local);
};

const getVendorEmail = (message: VendorQuoteMessage) => {
  if (message.vendorEmail?.trim()) return message.vendorEmail.trim();
  if (message.direction === 'outbound') return message.toEmail?.trim() || message.fromEmail?.trim() || '';
  return message.fromEmail?.trim() || message.toEmail?.trim() || '';
};

const getVendorName = (message: VendorQuoteMessage, vendorEmail: string) => {
  if (message.vendorName?.trim()) return message.vendorName.trim();
  const derived = deriveNameFromEmail(vendorEmail);
  return derived || 'Vendor';
};

const deriveThreadStatus = (messages: VendorThreadMessage[]): VendorThreadStatus => {
  const statusTokens = messages.map((message) => (message.status || '').toLowerCase());
  const hasClosed = statusTokens.some((status) => /closed|resolved|cancel|done/.test(status));
  if (hasClosed) return 'Closed';

  const hasDraft = statusTokens.some((status) => status.includes('draft'))
    || messages.some((message) => String(message.direction).toLowerCase() === 'draft');
  if (hasDraft) return 'Draft';

  const hasInbound = messages.some((message) => String(message.direction).toLowerCase() === 'inbound');
  const hasOutbound = messages.some((message) => String(message.direction).toLowerCase() === 'outbound');
  if (hasInbound && hasOutbound) return 'Replied';
  if (hasOutbound) return 'Sent';
  return hasInbound ? 'Replied' : 'Draft';
};

export const groupMessagesIntoThreads = (messages: VendorQuoteMessage[], queryId: number): VendorQuoteThread[] => {
  if (!Array.isArray(messages) || messages.length === 0) return [];

  const prepared: VendorThreadMessage[] = messages.map((message, index) => {
    const vendorEmail = getVendorEmail(message);
    return {
      ...message,
      sortValue: getSortValue(message.createdAt, index),
      preview: getMessagePreview(message),
      vendorEmail,
      vendorName: getVendorName(message, vendorEmail),
    };
  });

  const byThread = new Map<string, VendorThreadMessage[]>();
  prepared.forEach((message) => {
    const baseSubject = normalizeSubject(message.subject).toLowerCase();
    const explicitThreadId = message.threadId !== undefined && message.threadId !== null
      ? String(message.threadId).trim()
      : '';
    // Group primarily by an explicit thread id when present; otherwise fall back
    // to query + normalized subject so that replies and forwards for the same
    // RFQ stay in a single conversation, even if they come from / go to
    // different email addresses.
    const key = explicitThreadId || `${queryId}::${baseSubject}`;
    const bucket = byThread.get(key);
    if (bucket) {
      bucket.push(message);
    } else {
      byThread.set(key, [message]);
    }
  });

  const threads: VendorQuoteThread[] = [];
  byThread.forEach((threadMessages, key) => {
    const sorted = [...threadMessages].sort((a, b) => a.sortValue - b.sortValue || a.id - b.id);
    const latest = sorted[sorted.length - 1];
    const first = sorted[0];
    threads.push({
      id: key,
      subject: normalizeSubject(latest.subject || first.subject),
      vendor: {
        name: latest.vendorName || first.vendorName || 'Vendor',
        email: latest.vendorEmail || first.vendorEmail || '',
      },
      lastActivity: latest.createdAt || first.createdAt,
      messageCount: sorted.length,
      status: deriveThreadStatus(sorted),
      preview: latest.preview || '',
      messages: sorted,
    });
  });

  return threads.sort((a, b) => {
    const aValue = getSortValue(a.lastActivity, 0);
    const bValue = getSortValue(b.lastActivity, 0);
    return bValue - aValue;
  });
};
